import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpParamsOptions, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { environment } from '@env/environment';

import { map, tap } from 'rxjs/operators';

import { SortDirection } from '@angular/material/sort';

/*
const HttpUploadOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Headers': '*',
  }),
};
*/

/**
 * DATA STRUCTURES
 */

export interface Settings {
  EMPFANGER: string;
  SENDER: string;
  GEMEINDE: string;
  TATORTART: string;
  LANDESKENNZEICHEN: string;
  WAHRNEHMUNG: string;
  VSTV_PRE_NUMMER: string;
  UNTERZEICHNER: string;
  BEARBEITER: string;
  TAGE_ZUR_ANZEIGE: number;
  TAGE_ZUR_BOM: number;
  VSTV_BEWEISMITTEL_EXPORT?: string;
  MAIL_DEFAULT_TO?: string;
  MAIL_DEFAULT_BCC?: string;
  MAIL_DEFAULT_SUBJECT?: string;
  MAIL_DEFAULT_BODY?: string;
  WHITELIST_DEFAULT_REASON?: string;
  WHITELIST_NOTIFY_MOBILE?: string;
  WHITELIST_MOBILE_MESSAGE?: string;

  // Bulk-Import (Barcode) defaults - clone of the legacy C# "Barcode" tab
  BULK_STRAFE?: number;
  BULK_LAND?: string;
  BULK_BEZIRK?: string;
  BULK_ZONE?: number;
  BULK_FAHRZEUGART?: number;
  BULK_BEARBEITER?: string;

  // Report (Berichte) header/footer config - used by the Zahlungsabgleich payment report
  REPORT_COMPANY_NAME?: string;
  REPORT_ADDRESS?: string;
  REPORT_IBAN?: string;
  REPORT_FOOTER_TEXT?: string;
  REPORT_LOGO?: string;
  REPORT_PRINT_DATE?: boolean;
  REPORT_PRINT_PAGENUMBERS?: boolean;
  REPORT_PRINT_TOTALSUM?: boolean;

  MOBILE_PARKING_ENABLED?: boolean | string | number;
  MOBILE_PARKING_PROVIDER?: string;
  MOBILE_PARKING_API_KEY?: string;
  MOBILE_PARKING_SECURITY_TOKEN?: string;

  /** 1 = demo restrictions (no edit/bulk), 0 = normal */
  DEMO_MODE?: number | boolean;
}

export interface SystemInfoDienstnehmer {
  id: number;
  name: string;
  dienstnummer: number;
  dienstnummer_stvo: number;
  active: number;
  benutzergruppe: number;
  sachbereich: string;
}

export interface RolePermissionMatrix {
  roles: { id: number; name: string }[];
  modules: { id: string; label: string }[];
  matrix: { [roleId: number]: { [module: string]: boolean } };
}

export interface SystemInfo {
  server: {
    php_version: string;
    server_software: string;
    timezone: string;
    os: string;
  };
  database: {
    driver: string;
    host: string;
    database: string;
    mysql_version: string;
    charset: string;
    collation: string;
  };
  api: {
    webapp_id: string;
    gemeinde_key: string;
    accesspoint: string;
    api_base_url: string;
    api_request_url: string;
  };
  vstv: {
    empfanger: string;
    sender: string;
    gemeinde: string;
    tatortart: string;
    landeskennzeichen: string;
    sonstiger_strassenerhalter: string;
    wahrnehmung: string;
    vstv_pre_nummer: string;
    unterzeichner: string;
    bearbeiter: string;
    tage_zur_anzeige: number;
    tage_zur_bom: number;
  };
  mail: {
    host: string;
    port: string;
    type: string;
    sender_email: string;
    sender_name: string;
  };
  storage: {
    bilddatenbank: string;
    vstv_export: string;
    vstv25_export: string;
    webstore: string;
  };
  dienstnehmer: SystemInfoDienstnehmer[];
}

interface ApiResponse {
  data: Settings;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    // 'Access-Control-Allow-Headers': '*',
    'Access-Control-Allow-Origin': '*',
  }),
};

//
// --------------------------------------------------------------
//

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  errorMsg: string = '';

  constructor(private httpClient: HttpClient) {}

  /**
   * GET / FIND BY ID
   *
   * @return response()
   */
  getSettings(): Observable<Settings> {
    const apiUrl = `${environment.serverUrl}/settings`;

    return this.httpClient
      .get<ApiResponse>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          // 'Access-Control-Allow-Headers': '*',
          'Access-Control-Allow-Origin': '*',
        }),
        //params: new HttpParams().set('id', '' + id),
      })
      .pipe(map((response) => response.data));
  }

  /**
   * GET NEXT AKTENZEICHEN OVERVIEW (admin only)
   * Returns next Anzeige + BOM aktenzeichen for all Dienstnehmer
   *
   * @return response()
   */
  getNextAktenzeichenOverview(): Observable<any> {
    const apiUrl = `${environment.serverUrl}/nxakt-overview`;
    return this.httpClient.get<any>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }),
    });
  }

  /**
   * SET NEXT AKTENZEICHEN (admin only)
   * Creates a dummy t_transfer record to anchor the MAX value
   *
   * @return response()
   */
  setNextAktenzeichen(dienstnr_id: string, bom: number, aktenzeichen: string, name: string): Observable<any> {
    const body = { dienstnr_id, bom, aktenzeichen, name };
    console.log('>>> SettingsService.setNextAktenzeichen BODY:', JSON.stringify(body));
    console.log('>>> SettingsService.setNextAktenzeichen URL:', `${environment.serverUrl}/nxakt-set`);
    return this.httpClient.post<any>(
      `${environment.serverUrl}/nxakt-set`,
      JSON.stringify(body),
      httpOptions
    );
  }

  /**
   * GET SYSTEM INFO
   * Returns sanitized server, DB, mail, VSTV config, and Dienstnehmer data
   *
   * @return response()
   */
  getSystemInfo(): Observable<SystemInfo> {
    const apiUrl = `${environment.serverUrl}/systeminfo`;
    return this.httpClient
      .get<any>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        }),
      })
      .pipe(map((response) => response.data));
  }

  getRolePermissions(): Observable<RolePermissionMatrix> {
    const apiUrl = `${environment.serverUrl}/role-permissions`;
    return this.httpClient
      .get<any>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        }),
      })
      .pipe(map((response) => response?.data ?? response));
  }

  saveRolePermissions(matrix: { [roleId: number]: { [module: string]: boolean } }): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.serverUrl}/role-permissions`,
      JSON.stringify({ matrix }),
      httpOptions
    );
  }

  setSuperuser(su: number): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.serverUrl}/su-set`,
      JSON.stringify({ su }),
      httpOptions
    );
  }

  getDemoMode(): Observable<{ result: number; demo_mode: number }> {
    return this.httpClient.get<any>(`${environment.serverUrl}/demo-mode`, httpOptions);
  }

  setDemoMode(demoMode: number): Observable<{ result: number; demo_mode: number }> {
    return this.httpClient.post<any>(
      `${environment.serverUrl}/demo-mode`,
      JSON.stringify({ demo_mode: demoMode }),
      httpOptions
    );
  }

  /**
   * SEND AKT PER MAIL
   * Sends an email with mandate details and image links
   */
  sendAktMail(payload: any): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.serverUrl}/send-akt-mail`,
      JSON.stringify(payload),
      httpOptions
    );
  }

  /**
   * SYNC SETTINGS
   *
   * @return response()
   */

  public syncSettings(postData: any): Observable<Settings> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.httpClient.post<any>(environment.serverUrl + '/settings', JSON.stringify(postData), httpOptions).pipe(
      map((response) => {
        return {
          ...response.data,
        };
      }),
      tap((response: any) => {
        console.log(response);
        //this.repo.addQuiz(response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );

    /*
    .pipe(
      map((response: VisitorTable) => {
        //debugger;
        return response.data.map((item: Visitor) => ({
          ...item,
        }));
      }),
      tap((response: any) => {
        console.log('mrio');
        console.log(response);
        return <Visitor[]>response;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
        } else {
          errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
    */
  }

  /**
   * Write code on Method
   *
   * @return response()
   */
  private getServerErrorMessage(error: HttpErrorResponse) {
    console.log('custom service error handler');

    switch (error.status) {
      case 0: {
        // A client-side or network error occurred. Handle it accordingly.
        //console.error('An error occurred:', error.error);
        return `An error occurred: ${error.error}`;
      }
      case 404: {
        return `Not Found: ${error.message}`;
      }
      case 403: {
        return `Access Denied: ${error.message}`;
      }
      case 500: {
        return `Internal Server Error: ${error.message}`;
      }
      default: {
        return `Unknown Server Error: ${error.error}`;
      }

      // Return an observable with a user-facing error message.
      //return throwError(() => new Error('Something bad happened; please try again later.'));
    }
  }
}
